import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { useContatos } from "../../../hooks/useContatos";
import { apiService } from "../../../services/api";
import { Contato, CreateContatoData } from '../../../types/contato';
import { Offcanvas, Modal } from 'bootstrap';
import LocationSelector from './components/LocationSelector';
import MaskedInput from './components/MaskedInput';
import 'bootstrap/dist/css/bootstrap.min.css';
import { modalUtils } from '../../../utils/modalUtils';
import { ProtectedComponent } from "../../../components/ProtectedComponent";

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: Contato) => React.ReactNode;
  sorter?: (a: Contato, b: Contato) => number;
}

export const Contatos = () => {
  const dispatch = useDispatch();
  const { contatos, loading, error, refetchContatos } = useContatos();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingContatoId, setDeletingContatoId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredContatos, setFilteredContatos] = useState<Contato[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);

  // Estado para controlar o contato em edição
  const [editingContato, setEditingContato] = useState<Contato | null>(null);
  const [newContato, setNewContato] = useState<CreateContatoData>({
    nome: '',
    tipo_entidade: 'PF',
    email: '',
    telefone: '',
    bairro: ''
  });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredContatos(contatos);
  }, [contatos]);

  // Função para filtrar contatos quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredContatos(contatos);
        setNoResults(false);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = contatos.filter(contato => {
        // Campos a serem pesquisados
        const searchableFields = [
          contato.nome,
          contato.razao_social,
          contato.nome_fantasia,
          contato.email,
          contato.telefone,
          contato.whatsapp,
          contato.cpf,
          contato.cnpj,
          contato.municipio_nome,
          contato.endereco,
          contato.bairro
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredContatos(filtered);
      setNoResults(filtered.length === 0);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, contatos]);

  // Função para formatar telefone
  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Função para formatar CPF/CNPJ
  const formatDocument = (doc: string | undefined, tipo: 'PF' | 'PJ') => {
    if (!doc) return '';
    const cleaned = doc.replace(/\D/g, '');
    if (tipo === 'PF' && cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (tipo === 'PJ' && cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingContato(null);
  };

  // Função para limpar o estado do novo contato
  const handleCancelNew = () => {
    setNewContato({
      nome: '',
      tipo_entidade: 'PF',
      email: '',
      telefone: ''
    });
  };

  // Função para preparar a edição
  const handleEdit = (contato: Contato) => {
    setEditingContato(contato);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (contato: Contato) => {
    setDeletingContatoId(contato.id);
  };

  const columns: TableColumn[] = [
    {
      title: "Nome/Razão Social",
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.tipo_entidade === 'PJ' ? record.razao_social : record.nome}</div>
          {record.tipo_entidade === 'PJ' && record.nome_fantasia && (
            <small className="text-muted">{record.nome_fantasia}</small>
          )}
        </div>
      ),
      sorter: (a, b) => (a.tipo_entidade === 'PJ' ? a.razao_social || '' : a.nome)
        .localeCompare(b.tipo_entidade === 'PJ' ? b.razao_social || '' : b.nome),
    },
    {
      title: "Documento",
      render: (_, record) => formatDocument(record.tipo_entidade === 'PF' ? record.cpf : record.cnpj, record.tipo_entidade),
    },
    {
      title: "Contato",
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div>{formatPhone(record.telefone)}</div>
          {record.whatsapp && <div className="text-success"><i className="ti ti-brand-whatsapp"></i> {formatPhone(record.whatsapp)}</div>}
        </div>
      ),
    },
    {
      title: "Localização",
      render: (_, record) => (
        <div>
          {record.municipio_nome && (
            <div>{record.municipio_nome} - {record.municipio_uf}</div>
          )}
          {record.endereco && (
            <small className="text-muted">
              {record.endereco}
              {record.bairro && `, ${record.bairro}`}
              {record.numero && `, ${record.numero}`}
              {record.complemento && ` - ${record.complemento}`}
            </small>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => (
        <div>
          {ativo === 1 ? (
            <span className="badge badge-pill badge-status bg-success">Ativo</span>
          ) : (
            <span className="badge badge-pill badge-status bg-danger">Inativo</span>
          )}
        </div>
      ),
      sorter: (a, b) => a.ativo - b.ativo,
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="CONFIG_CLIENTES" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="CONFIG_CLIENTES" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_contact"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>

      ),
    }
  ];

  // Função para limpar modal
  const limparModal = (idModal: string) => {
    // Usar o utilitário corrigido
    modalUtils.fecharModal(idModal, () => {
      // Código específico para quando o modal é fechado
      if (idModal === 'success_delete_modal') {
        setDeletingContatoId(null);
      }
    });
  };

  // Função para criar novo contato
  const handleCreateContato = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      // Remove formatações dos campos antes de enviar
      const cleanData = {
        ...newContato,
        telefone: newContato.telefone?.replace(/\D/g, ''),
        whatsapp: newContato.whatsapp?.replace(/\D/g, ''),
        cnpj: newContato.cnpj?.replace(/\D/g, ''),
        cpf: newContato.cpf?.replace(/\D/g, ''),
        cep: newContato.cep?.replace(/\D/g, '')
      };

      // Validação por tipo de entidade
      if (cleanData.tipo_entidade === 'PF') {
        if (!cleanData.nome?.trim()) {
          alert('O nome é obrigatório para Pessoa Física');
          return;
        }
      } else {
        if (!cleanData.razao_social?.trim()) {
          alert('A razão social é obrigatória para Pessoa Jurídica');
          return;
        }
      }

      // Validações comuns
      if (!cleanData.email?.trim()) {
        alert('O email é obrigatório');
        return;
      }

      if (!cleanData.telefone) {
        alert('O telefone é obrigatório');
        return;
      }

      // Preparar dados base
      const baseData = {
        tipo_entidade: cleanData.tipo_entidade,
        // Para PJ, usamos razao_social como nome, para PF usamos o nome direto
        nome: cleanData.tipo_entidade === 'PF' ? cleanData.nome?.trim() : cleanData.razao_social?.trim() || '',
        email: cleanData.email.trim(),
        telefone: cleanData.telefone,
        ...(cleanData.whatsapp && { whatsapp: cleanData.whatsapp }),
        ...(cleanData.endereco && { endereco: cleanData.endereco.trim() }),
        ...(cleanData.numero && { numero: cleanData.numero.trim() }),
        ...(cleanData.complemento && { complemento: cleanData.complemento.trim() }),
        ...(cleanData.cep && { cep: cleanData.cep }),
        ...(cleanData.estado && { estado: cleanData.estado }),
        ...(cleanData.id_municipio && { id_municipio: cleanData.id_municipio }),
        ...(cleanData.observacoes && { observacoes: cleanData.observacoes.trim() })
      };

      // Adicionar campos específicos do tipo
      const contatoData: CreateContatoData = {
        ...baseData,
        ...(cleanData.tipo_entidade === 'PF'
          ? {
            cpf: cleanData.cpf,
          }
          : {
            razao_social: cleanData.razao_social?.trim(),
            nome_fantasia: cleanData.nome_fantasia?.trim(),
            cnpj: cleanData.cnpj,
          }
        )
      };

      console.log('Dados limpos a serem enviados:', contatoData);

      // Envia para a API
      await apiService.createContato(contatoData);
      await refetchContatos();

      handleCancelNew();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_contact');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');

        const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        const successModal = document.getElementById('success_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao criar contato:', error);
      if (error instanceof Error) {
        alert(`Erro ao criar o contato: ${error.message}`);
      } else {
        alert('Erro ao criar o contato. Por favor, verifique os dados e tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar contato
  const handleUpdateContato = async () => {
    if (!editingContato?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateContato(editingContato.id, editingContato);
      await refetchContatos();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');

        const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        const successModal = document.getElementById('success_edit_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar contato:', error);
      alert('Erro ao atualizar o contato. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar contato
  const handleDeleteContato = async () => {
    if (!deletingContatoId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteContato(deletingContatoId);
      await refetchContatos();

      // Fechar o modal de confirmação usando o utilitário
      modalUtils.fecharModal('delete_contact');

      // Após um breve delay, mostrar o modal de sucesso
      setTimeout(() => {
        modalUtils.abrirModal('success_delete_modal');
      }, 400);

    } catch (error) {
      console.error('Erro ao excluir contato:', error);
      alert('Erro ao excluir o contato. Por favor, tente novamente.');

      // Garantir limpeza mesmo em caso de erro
      modalUtils.limparEfeitosModal();
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup ao desmontar o componente
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open', 'offcanvas-open');
      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
    };
  }, []);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Clientes<span className="count-title">{filteredContatos.length} clientes</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Cliente"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Recentes
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Antigos
                          </Link>
                        </div>
                      </div>

                      {/* Filtro Tipo */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-user me-2" />
                          Tipo
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-user me-1" />
                            Pessoa Física
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-building me-1" />
                            Pessoa Jurídica
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="CONFIG_CLIENTES" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_contact"
                        >
                          <i className="ti ti-user-plus me-2" />
                          Adicionar Cliente
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredContatos.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum contato encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredContatos.map(contato => ({
                          ...contato,
                          key: contato.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Novo Contato */}

      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_contact">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Contato</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateContato}>
            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Pessoa <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={newContato.tipo_entidade}
                onChange={(e) => setNewContato(prev => ({
                  ...prev,
                  tipo_entidade: e.target.value as 'PF' | 'PJ'
                }))}
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>

            {newContato.tipo_entidade === 'PF' ? (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newContato.nome}
                    onChange={(e) => setNewContato(prev => ({
                      ...prev,
                      nome: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CPF</label>
                  <MaskedInput
                    value={newContato.cpf || ''}
                    onChange={(value) => setNewContato(prev => ({
                      ...prev,
                      cpf: value
                    }))}
                    mask="cpf"
                    placeholder="000.000.000-00"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Razão Social <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newContato.razao_social || ''}
                    onChange={(e) => setNewContato(prev => ({
                      ...prev,
                      razao_social: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Nome Fantasia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newContato.nome_fantasia || ''}
                    onChange={(e) => setNewContato(prev => ({
                      ...prev,
                      nome_fantasia: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CNPJ</label>
                  <MaskedInput
                    value={newContato.cnpj || ''}
                    onChange={(value) => setNewContato(prev => ({
                      ...prev,
                      cnpj: value
                    }))}
                    mask="cnpj"
                    placeholder="00.000.000/0000-00"
                  />
                </div>
              </>
            )}

            <div className="mb-3">
              <label className="col-form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={newContato.email}
                onChange={(e) => setNewContato(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Telefone <span className="text-danger">*</span>
              </label>
              <MaskedInput
                value={newContato.telefone || ''}
                onChange={(value) => setNewContato(prev => ({
                  ...prev,
                  telefone: value
                }))}
                mask="phone"
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">WhatsApp</label>
              <MaskedInput
                value={newContato.whatsapp || ''}
                onChange={(value) => setNewContato(prev => ({
                  ...prev,
                  whatsapp: value
                }))}
                mask="phone"
                placeholder="(00) 00000-0000"
              />
            </div>



            <LocationSelector
              selectedState={newContato.estado}
              selectedMunicipality={newContato.id_municipio ? Number(newContato.id_municipio) : undefined}
              onStateChange={(estado) => {

                setNewContato(prev => ({
                  ...prev,
                  estado: estado,
                  id_municipio: undefined
                }));
              }}
              onMunicipalityChange={(municipioId) => {

                setNewContato(prev => ({
                  ...prev,
                  id_municipio: Number(municipioId)
                }));
              }}
              apiService={apiService}
            />


            <div className="mb-3">
              <label className="col-form-label">Endereço</label>
              <input
                type="text"
                className="form-control"
                value={newContato.endereco || ''}
                onChange={(e) => setNewContato(prev => ({
                  ...prev,
                  endereco: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Bairro</label>
              <input
                type="text"
                className="form-control"
                value={newContato.bairro || ''}
                onChange={(e) => setNewContato(prev => ({
                  ...prev,
                  bairro: e.target.value
                }))}
              />
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <label className="col-form-label">Número</label>
                <input
                  type="text"
                  className="form-control"
                  value={newContato.numero || ''}
                  onChange={(e) => setNewContato(prev => ({
                    ...prev,
                    numero: e.target.value
                  }))}
                />
              </div>
              <div className="col-8">
                <label className="col-form-label">Complemento</label>
                <input
                  type="text"
                  className="form-control"
                  value={newContato.complemento || ''}
                  onChange={(e) => setNewContato(prev => ({
                    ...prev,
                    complemento: e.target.value
                  }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">CEP</label>
              <MaskedInput
                value={newContato.cep || ''}
                onChange={(value) => setNewContato(prev => ({
                  ...prev,
                  cep: value
                }))}
                mask="cep"
                placeholder="00000-000"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Observações</label>
              <textarea
                className="form-control"
                value={newContato.observacoes || ''}
                onChange={(e) => setNewContato(prev => ({
                  ...prev,
                  observacoes: e.target.value
                }))}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>


      {/* Offcanvas Editar Contato */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Contato</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateContato();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Pessoa <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={editingContato?.tipo_entidade || 'PF'}
                onChange={(e) => setEditingContato(prev => prev ? {
                  ...prev,
                  tipo_entidade: e.target.value as 'PF' | 'PJ'
                } : null)}
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>

            {editingContato?.tipo_entidade === 'PF' ? (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingContato.nome || ''}
                    onChange={(e) => setEditingContato(prev => prev ? {
                      ...prev,
                      nome: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CPF</label>
                  <MaskedInput
                    value={editingContato?.cpf || ''}
                    onChange={(value) => setEditingContato(prev => prev ? {
                      ...prev,
                      cpf: value
                    } : null)}
                    mask="cpf"
                    placeholder="000.000.000-00"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Razão Social <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingContato?.razao_social || ''}
                    onChange={(e) => setEditingContato(prev => prev ? {
                      ...prev,
                      razao_social: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Nome Fantasia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingContato?.nome_fantasia || ''}
                    onChange={(e) => setEditingContato(prev => prev ? {
                      ...prev,
                      nome_fantasia: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CNPJ</label>
                  <MaskedInput
                    value={editingContato?.cnpj || ''}
                    onChange={(value) => setEditingContato(prev => prev ? {
                      ...prev,
                      cnpj: value
                    } : null)}
                    mask="cnpj"
                    placeholder="00.000.000/0000-00"
                  />
                </div>
              </>
            )}

            <div className="mb-3">
              <label className="col-form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={editingContato?.email || ''}
                onChange={(e) => setEditingContato(prev => prev ? {
                  ...prev,
                  email: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Telefone <span className="text-danger">*</span>
              </label>
              <MaskedInput
                value={editingContato?.telefone || ''}
                onChange={(value) => setEditingContato(prev => prev ? {
                  ...prev,
                  telefone: value
                } : null)}
                mask="phone"
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">WhatsApp</label>
              <MaskedInput
                value={editingContato?.whatsapp || ''}
                onChange={(value) => setEditingContato(prev => prev ? {
                  ...prev,
                  whatsapp: value
                } : null)}
                mask="phone"
                placeholder="(00) 00000-0000"
              />
            </div>


            <LocationSelector
              selectedState={editingContato?.estado}
              selectedMunicipality={editingContato?.id_municipio}
              onStateChange={(estado) => setEditingContato(prev => prev ? {
                ...prev,
                estado: estado,
                id_municipio: undefined
              } : null)}
              onMunicipalityChange={(municipioId) => setEditingContato(prev => prev ? {
                ...prev,
                id_municipio: Number(municipioId)
              } : null)}
              apiService={apiService}
              isEditing={false}
            />

            <div className="mb-3">
              <label className="col-form-label">Endereço</label>
              <input
                type="text"
                className="form-control"
                value={editingContato?.endereco || ''}
                onChange={(e) => setEditingContato(prev => prev ? {
                  ...prev,
                  endereco: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Bairro</label>
              <input
                type="text"
                className="form-control"
                value={editingContato?.bairro || ''}
                onChange={(e) => setEditingContato(prev => prev ? {
                  ...prev,
                  bairro: e.target.value
                } : null)}
              />
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <label className="col-form-label">Número</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingContato?.numero || ''}
                  onChange={(e) => setEditingContato(prev => prev ? {
                    ...prev,
                    numero: e.target.value
                  } : null)}
                />
              </div>
              <div className="col-8">
                <label className="col-form-label">Complemento</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingContato?.complemento || ''}
                  onChange={(e) => setEditingContato(prev => prev ? {
                    ...prev,
                    complemento: e.target.value
                  } : null)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">CEP</label>
              <MaskedInput
                value={editingContato?.cep || ''}
                onChange={(value) => setEditingContato(prev => prev ? {
                  ...prev,
                  cep: value
                } : null)}
                mask="cep"
                placeholder="00000-000"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Observações</label>
              <textarea
                className="form-control"
                value={editingContato?.observacoes || ''}
                onChange={(e) => setEditingContato(prev => prev ? {
                  ...prev,
                  observacoes: e.target.value
                } : null)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={() => setEditingContato(null)}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_contact" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Contato?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o contato selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingContatoId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteContato}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Criação */}
      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Contato Criado com Sucesso!</h3>
                <p>O contato foi criado e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Contato Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Contato Excluído com Sucesso!</h3>
                <p>O contato foi removido permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};